import React, { useState } from 'react';
import { Alert, Button, Modal, Space, Tag, Typography } from 'antd';
import { BankOutlined, DeleteOutlined, LockOutlined, MedicineBoxOutlined, PlusOutlined } from '@ant-design/icons';
import { deleteMedication } from '../../../../../../api/healthcare/MedicationsApi';
import CreateMedicationModal from '../../../../patient/modalComponents/submodals/CreateMedicationModal';
import { Medication, defaultMedication } from '../../../../../../types/healthcare/MedicationTypes';
import type { UserUuid } from '../../../../../../types/identity';

const { Text } = Typography;

const FREQUENCY_LABELS: Record<string, string> = {
  once_daily:   'Once daily',
  twice_daily:  'Twice daily',
  three_daily:  'Three times daily',
  four_daily:   'Four times daily',
  as_needed:    'PRN',
  weekly:       'Weekly',
  fortnightly:  'Fortnightly',
  monthly:      'Monthly',
};

interface DispenseDestination {
  approved_pharmacy_id: number;
  pharmacy_name: string;
  pharmacy_code?: string;
}

interface Props {
  taskConfig: Record<string, any>;
  patientId: UserUuid;
  onChange: (payload: Record<string, any>, isValid: boolean) => void;
  /** Set when a select_dispensing_pharmacy upstream task has recorded a destination (Pathway A). */
  dispenseDestination?: DispenseDestination;
}

const PrepareMedicationTaskForm: React.FC<Props> = ({ taskConfig, patientId, onChange, dispenseDestination }) => {
  const [medications, setMedications]             = useState<Medication[]>([]);
  const [modalOpen, setModalOpen]                 = useState(false);
  const [currentMedication, setCurrentMedication] = useState<Medication>({ ...defaultMedication });

  const suggestedName: string | undefined = taskConfig.medication_name;

  const openAdd = () => {
    setCurrentMedication({ ...defaultMedication, patient_id: patientId, medication_name: suggestedName ?? '' });
    setModalOpen(true);
  };

  const handleSaved = (saved: Medication) => {
    const next = [...medications, saved];
    setMedications(next);
    setModalOpen(false);
    onChange({ medication_ids: next.map((m) => m.id) }, true);
  };

  const handleRemove = (med: Medication) => {
    Modal.confirm({
      title: 'Remove Medication',
      content: 'This will delete the medication record. Are you sure?',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try { await deleteMedication(med.id); } catch {}
        const next = medications.filter((m) => m.id !== med.id);
        setMedications(next);
        onChange({ medication_ids: next.map((m) => m.id) }, next.length > 0);
      },
    });
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }} size={6}>
      {dispenseDestination && (
        <Alert
          type="info"
          icon={<LockOutlined />}
          showIcon
          message={
            <span style={{ fontSize: 11 }}>
              Script destination:{' '}
              <strong style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <BankOutlined />
                {dispenseDestination.pharmacy_name}
                {dispenseDestination.pharmacy_code && (
                  <Tag style={{ margin: 0, fontSize: 10, padding: '0 4px' }}>
                    {dispenseDestination.pharmacy_code}
                  </Tag>
                )}
              </strong>
              {' '}— not released to patient.
            </span>
          }
          style={{ padding: '4px 8px' }}
        />
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Text style={{ fontSize: 11, fontWeight: 600, color: 'var(--mg-text)' }}>Medications</Text>
        <Button type="primary" size="small" icon={<PlusOutlined />} onClick={openAdd}>
          Add
        </Button>
      </div>

      {suggestedName && (
        <Space size={4}>
          <Text style={{ fontSize: 11, color: 'var(--mg-text-secondary)' }}>Suggested:</Text>
          <Tag icon={<MedicineBoxOutlined />} style={{ margin: 0, fontSize: 11 }}>{suggestedName}</Tag>
        </Space>
      )}

      {medications.length === 0 ? (
        <Text type="danger" style={{ fontSize: 11, display: 'block', padding: '4px 0' }}>
          At least one medication is required.
        </Text>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {medications.map((med) => (
            <div key={med.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, padding: '5px 8px', borderRadius: 4, border: '1px solid var(--mg-border)', background: 'var(--mg-bg)' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                  <Text style={{ fontSize: 12, fontWeight: 600 }}>{med.medication_name}</Text>
                  {med.dosage && <Tag color="blue" style={{ margin: 0, fontSize: 10, padding: '0 4px' }}>{med.dosage}</Tag>}
                  {med.frequency && <Tag style={{ margin: 0, fontSize: 10, padding: '0 4px' }}>{FREQUENCY_LABELS[med.frequency] ?? med.frequency}</Tag>}
                  {med.script_status === 'pending' && <Tag color="orange" style={{ margin: 0, fontSize: 10, padding: '0 4px' }}>Script req.</Tag>}
                </div>
                {med.instructions && (
                  <Text style={{ fontSize: 10, color: 'var(--mg-text-secondary)', display: 'block', marginTop: 1 }}>
                    {med.instructions}
                  </Text>
                )}
              </div>
              <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => handleRemove(med)} />
            </div>
          ))}
        </div>
      )}

      {modalOpen && (
        <CreateMedicationModal
          currentMedication={currentMedication}
          setCurrentMedication={setCurrentMedication}
          isEditing={false}
          isVisible={modalOpen}
          onClose={() => setModalOpen(false)}
          isConsult={false}
          onSaved={handleSaved}
        />
      )}
    </Space>
  );
};

export default PrepareMedicationTaskForm;
